import { useTranslation } from "react-i18next"
import { CalendarBlank, Plus } from "@phosphor-icons/react"
import { PATIENT_AVATAR_NAME } from "@/components/avatar-user-tooltip"
import { Button } from "@/components/ui/button"
import { CardBodyContent, CardBodyHeader, CardBodyHeaderTitle } from "@/components/ui/card"
import { FaroInsightsWrapper } from "@/grafana-faro"
import { useUserStore } from "@/lib/fetch-users"
import { formatDate, formatDateString } from "@/lib/format-date"
import { formatInServerTimeZone } from "@/lib/initial-time-zone"
import { getSlotLabel } from "@/lib/slot"
import { cn } from "@/lib/utils"
import { TeleconsultationDrawer } from "@/modules/patients/agenda/teleconsultation/booking/teleconsultation-drawer"
import { ProgressNoteReadOnlyCardSection } from "@/modules/patients/detail/progress-note/components/progress-note-readonly-card-section"
import { ProgressNoteAndParentData } from "@/modules/patients/detail/progress-note/progress-note-card"
import { ProgressNoteData } from "@/modules/patients/detail/progress-note/types/progress-note-fragment-type"
import { useDrawerStore } from "@/stores/useDrawerStore"
import { Country } from "@/types/__generated__/union-types"

const formatUserName = (user: { firstName: string; lastName: string } | null | undefined) =>
  user ? `${user.firstName} ${user.lastName}` : ""

interface ProgressNoteFollowUpProps {
  progressNote: ProgressNoteData
  countryCode: Country
  patientId: string
}

const ProgressNoteFollowUp = ({ countryCode, patientId, progressNote }: ProgressNoteFollowUpProps) => {
  const { t } = useTranslation()
  const openDrawer = useDrawerStore((state) => state.openDrawer)
  const users = useUserStore((state) => state.users)

  const followUpAppointment = progressNote?.followUpAppointment
  const assignedTo = followUpAppointment?.assignedTo ? users?.get(followUpAppointment.assignedTo) : undefined
  const cancelledBy = followUpAppointment?.takenBy ? users?.get(followUpAppointment.takenBy) : undefined

  const handleOpenDrawer = () => {
    openDrawer(
      () => (
        <TeleconsultationDrawer
          mode={{ kind: "FOLLOWUP_FOR_PROGRESS_NOTE", data: { progressNote } }}
          countryCode={countryCode}
          patientId={patientId}
          appointmentId={null}
        />
      ),
      "TeleconsultationDrawer",
    )
  }

  return (
    <>
      <CardBodyHeader data-testid="follow-up-title">
        <CardBodyHeaderTitle>{t("episodeOfCare.progressNote.followUp")}</CardBodyHeaderTitle>
        {(!followUpAppointment || followUpAppointment.status === "CANCELLED") && (
          <div className="pr-2">
            <Button
              variant="tertiary"
              className="h-10 w-10 p-2"
              onClick={() => {
                FaroInsightsWrapper.pushEvent("follow_up_open")
                handleOpenDrawer()
              }}
              data-testid="follow-up-button">
              <Plus className="text-on-surface-subdued" />
            </Button>
          </div>
        )}
      </CardBodyHeader>
      {followUpAppointment && (
        <CardBodyContent>
          <div className="flex items-center gap-4 px-4">
            <CalendarBlank className="text-on-surface-subdued" />
            <div className="grid">
              <span
                className={cn("text-on-surface", followUpAppointment.status === "CANCELLED" && "line-through")}
                data-testid="follow-up-appointment-label">
                {t("episodeOfCare.progressNote.followUpAppointment")}
              </span>
              <span
                className="text-on-surface-subdued text-xs leading-4 tracking-[0.25px]"
                data-testid="follow-up-appointment-info">
                {followUpAppointment.initialBookedDateTime &&
                  formatInServerTimeZone(followUpAppointment.initialBookedDateTime, "dd.MM.yyyy HH:mm")}
                {followUpAppointment.initialAssignedToUser &&
                  ` • ${formatUserName(followUpAppointment.initialAssignedToUser)}`}
              </span>

              {followUpAppointment.rescheduledAt && followUpAppointment.status !== "CANCELLED" && (
                <>
                  <span
                    className="text-on-surface-subdued text-xs leading-4 tracking-[0.25px]"
                    data-testid="follow-up-rescheduled-to">
                    {t("episodeOfCare.progressNote.rescheduledTo", {
                      date: `${formatDate(new Date(followUpAppointment.date))} ${getSlotLabel(followUpAppointment.slotId)}`,
                      user: formatUserName(assignedTo),
                    })}
                  </span>
                  <span
                    className="text-on-surface-subdued text-xs leading-4 tracking-[0.25px]"
                    data-testid="follow-up-changed-on">
                    {t("episodeOfCare.progressNote.changedOn", {
                      date: formatDateString(followUpAppointment.rescheduledAt),
                      user:
                        followUpAppointment.rescheduledBy === PATIENT_AVATAR_NAME
                          ? t("episodeOfCare.progressNote.patient")
                          : formatUserName(followUpAppointment.rescheduledByUser),
                    })}
                  </span>
                </>
              )}

              {followUpAppointment.status === "CANCELLED" && (
                <span
                  className="text-on-surface-subdued text-xs leading-4 tracking-[0.25px]"
                  data-testid="follow-up-cancelled-by">
                  {t("episodeOfCare.progressNote.cancelledBy", {
                    user:
                      followUpAppointment.takenBy === PATIENT_AVATAR_NAME
                        ? t("episodeOfCare.progressNote.patient")
                        : formatUserName(cancelledBy),
                    date: followUpAppointment.completedAt ? formatDateString(followUpAppointment.completedAt) : "",
                  })}
                </span>
              )}
            </div>
          </div>
        </CardBodyContent>
      )}
    </>
  )
}

export type FollowUpAppointment = ProgressNoteAndParentData["episodeOfCareProgressNoteData"]["followUpAppointment"]

interface FollowUpAppointmentReadOnlySectionProps {
  followUpAppointment: FollowUpAppointment
}

const FollowUpAppointmentReadOnlySection = ({ followUpAppointment }: FollowUpAppointmentReadOnlySectionProps) => {
  const { t } = useTranslation()
  const users = useUserStore((state) => state.users)
  const assignedTo = followUpAppointment?.assignedTo ? users?.get(followUpAppointment.assignedTo) : undefined
  const cancelledBy = followUpAppointment?.takenBy ? users?.get(followUpAppointment.takenBy) : undefined

  const isCancelled = followUpAppointment?.status === "CANCELLED"
  const isRescheduled = followUpAppointment?.rescheduledAt !== null

  return (
    <ProgressNoteReadOnlyCardSection
      className="border-t"
      title={t("episodeOfCare.progressNote.followUp")}
      testId="follow-up-read-only">
      {followUpAppointment && (
        <div className="flex flex-row items-center gap-4 px-4">
          <CalendarBlank className="text-on-surface-subdued" />
          <div className="grid gap-1">
            <span
              className={cn("text-on-surface", isCancelled && "line-through")}
              data-testid="follow-up-appointment-label-read-only">
              {t("episodeOfCare.progressNote.followUpAppointment")}
            </span>
            <span
              className="text-on-surface-subdued text-xs leading-4 tracking-[0.25px]"
              data-testid="follow-up-appointment-info-read-only">
              {formatDateString(followUpAppointment.initialBookedDateTime)}
              {followUpAppointment.initialAssignedToUser &&
                ` • ${formatUserName(followUpAppointment.initialAssignedToUser)}`}
            </span>

            {isRescheduled && !isCancelled && (
              <>
                <span
                  className="text-on-surface-subdued text-xs leading-4 tracking-[0.25px]"
                  data-testid="follow-up-rescheduled-to">
                  {t("episodeOfCare.progressNote.rescheduledTo", {
                    date: `${formatDate(new Date(followUpAppointment.date))} ${getSlotLabel(followUpAppointment.slotId)}`,
                    user: formatUserName(assignedTo),
                  })}
                </span>
                <span
                  className="text-on-surface-subdued text-xs leading-4 tracking-[0.25px]"
                  data-testid="follow-up-changed-on">
                  {t("episodeOfCare.progressNote.changedOn", {
                    date: formatDateString(followUpAppointment.rescheduledAt),
                    user:
                      followUpAppointment.rescheduledBy === PATIENT_AVATAR_NAME
                        ? t("episodeOfCare.progressNote.patient")
                        : formatUserName(followUpAppointment.rescheduledByUser),
                  })}
                </span>
              </>
            )}

            {isCancelled && (
              <span
                className="text-on-surface-subdued text-xs leading-4 tracking-[0.25px]"
                data-testid="follow-up-cancelled-by">
                {t("episodeOfCare.progressNote.cancelledBy", {
                  user:
                    followUpAppointment.takenBy === PATIENT_AVATAR_NAME
                      ? t("episodeOfCare.progressNote.patient")
                      : formatUserName(cancelledBy),
                  date: followUpAppointment.completedAt ? formatDateString(followUpAppointment.completedAt) : "",
                })}
              </span>
            )}
          </div>
        </div>
      )}
    </ProgressNoteReadOnlyCardSection>
  )
}

export { FollowUpAppointmentReadOnlySection, ProgressNoteFollowUp }
